import Controller from './application_controller'

/**
 * Tracks Turbo link navigations and the request state.
 *
 * Also re-broadcasts genuine page visits as `turbo:page-load` on the document.
 * Unlike `turbo:load`, it does NOT fire for page-driving frame visits (e.g.
 * filter/tab changes using `data-turbo-action`), which render a frame but not
 * the page. Listen via `turbo:page-load@document` when you only want real page
 * navigations and back/forward restores.
 */
export default class extends Controller {
  navigationEvent = null
  navigationTarget = null
  visit = null

  connect() {
    this.addEventListener('turbo:click', event => this.recordNavigation(event))
    this.addEventListener('turbo:before-fetch-request', event => this.markBusyIfNavigationRequest(event))
    this.addEventListener('turbo:before-fetch-response', event => this.clearNavigationAfterResponse(event))
    this.addEventListener('turbo:fetch-request-error', event => this.clearNavigationAfterResponse(event))
    this.addEventListener('turbo:before-cache', event => this.clearNavigation())

    this.addEventListener('turbo:visit', event => this.trackVisit(event))
    this.addEventListener('turbo:render', () => this.markVisitRendered())
    this.addEventListener('turbo:load', () => this.emitPageLoad())
  }

  disconnect() {
    this.clearNavigation()
    super.disconnect()
  }

  recordNavigation(event) {
    this.navigationEvent = event
  }

  markBusyIfNavigationRequest(event) {
    if (this.isPrefetchRequest(event)) {
      return
    }

    if (event.detail.url == this.navigationEvent?.detail.url) {
      this.markNavigationAsBusy(event.target)
    } else {
      this.clearNavigation()
    }
  }

  async clearNavigationAfterResponse(event) {
    if (this.isPrefetchRequest(event) || this.navigationTarget !== event.target) {
      return
    }

    // Errors won't have a fetchResponse
    if (event.detail.fetchResponse) {
      await event.detail.fetchResponse.responseHTML
    }

    this.clearNavigation()
  }

  markNavigationAsBusy(target) {
    this.navigationTarget = target
    this.navigationEvent?.target?.setAttribute('aria-busy', 'true');
  }

  clearNavigation() {
    this.navigationEvent?.target?.removeAttribute('aria-busy');
    delete this.navigationEvent
    delete this.navigationTarget
  }

  isPrefetchRequest(event) {
    return !event.target.matches('html, turbo-frame, form')
  }

  // A Turbo visit started — track it so we can classify it once it completes.
  trackVisit(event) {
    this.visit = { ...event.detail, rendered: false }
  }

  // turbo:render only fires when Turbo actually re-renders the page. Page-driving
  // frame visits (willRender: false) never reach here, so they stay rendered: false.
  markVisitRendered() {
    if (this.visit) {
      this.visit.rendered = true
    }
  }

  // On completion, re-broadcast only visits that rendered the page. Frame visits
  // (filter/tab changes) and the initial cold load are skipped.
  emitPageLoad() {
    const visit = this.visit
    this.visit = null

    if (!visit?.rendered) {
      return
    }

    this.dispatch('page-load', {
      prefix: 'turbo',
      target: document,
      detail: { url: visit.url, action: visit.action }
    })
  }
}
